import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import {
  BsEye,
  BsPencilSquare,
  BsPersonFill,
  BsPlusLg,
  BsSearch,
  BsTrash,
  BsX,
} from "react-icons/bs";

import Sidebar from "../../components/sidebar/Sidebar";
import { getImageUrl } from "../../utils/helper";
import {
  serviceGetAllUsers,
  // serviceDeleteUser,
} from "../../api/serviceApi";

import "./userlist.css";

const ITEMS_PER_PAGE = 15;

const UserList = () => {
  const searchRef = useRef(null);

  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [enlargedImageUrl, setEnlargedImageUrl] = useState(null);

  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);

  // ==========================================================
  // LOAD USERS
  // ==========================================================

  useEffect(() => {
    let cancelled = false;

    const loadUsers = async () => {
      try {
        setLoading(true);

        const result = await serviceGetAllUsers();

        if (cancelled) {
          return;
        }

        setData(Array.isArray(result) ? result : result?.data || []);
      } catch (error) {
        console.error("Error fetching users:", error);

        if (!cancelled) {
          toast.error(error?.message || "Failed to load employees.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, []);

  // ==========================================================
  // SEARCH
  // ==========================================================

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return data;
    }

    return data.filter((user) => {
      const haystack = [
        user?.name,
        user?.username,
        user?.role,
        user?.email,
        user?.phone,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [data, searchQuery]);

  // ==========================================================
  // PAGINATION
  // ==========================================================

  const totalPages = Math.max(
    1,
    Math.ceil(filteredUsers.length / ITEMS_PER_PAGE),
  );

  const currentUsers = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;

    return filteredUsers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // ==========================================================
  // CLEAR SEARCH
  // ==========================================================

  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);

    searchRef.current?.focus();
  };

  // ==========================================================
  // DELETE USER
  //
  // Enable once serviceDeleteUser() exists.
  // ==========================================================

  const onDelete = async (user) => {
    const confirmed = window.confirm(
      `Delete ${
        user.name || user.username || "this employee"
      }? This cannot be undone.`,
    );

    if (!confirmed) {
      return;
    }

    toast.info("Delete user API is not connected yet.");

    /*
    try {
      await serviceDeleteUser(user.id);

      setData((previous) =>
        previous.filter(
          (item) => item.id !== user.id
        )
      );

      toast.success(
        "Employee deleted successfully."
      );

    } catch (error) {
      console.error(
        "Error deleting employee:",
        error
      );

      toast.error(
        error?.message ||
          "Failed to delete employee."
      );
    }
    */
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="employee-page">
      <Sidebar />

      <main className="employee-content">
        <div className="employee-container">
          {/* HEADER */}

          <header className="employee-header">
            <div>
              <h1>Employees</h1>

              <p>Manage employee accounts, roles and contact details.</p>
            </div>

            <Link to="/employees/new" className="employee-btn primary">
              <BsPlusLg />

              <span>Add Employee</span>
            </Link>
          </header>

          {/* SEARCH */}

          <section className="employee-toolbar">
            <div className="employee-search">
              <label htmlFor="employee-search">Search</label>

              <div className="employee-search-control">
                <BsSearch className="employee-search-icon" />

                <input
                  ref={searchRef}
                  id="employee-search"
                  type="text"
                  placeholder="Name, username, email, phone or role..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />

                {searchQuery && (
                  <button
                    type="button"
                    className="employee-search-clear"
                    onClick={clearSearch}
                    aria-label="Clear search"
                    title="Clear search"
                  >
                    <BsX />
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* LOADING */}

          {loading ? (
            <div className="employee-state">
              <div className="employee-spinner" />

              <span>Loading employees...</span>
            </div>
          ) : (
            <section className="employee-table-card">
              {/* TABLE META */}

              <div className="employee-table-meta">
                <span>
                  Showing <strong>{currentUsers.length}</strong> of{" "}
                  <strong>{filteredUsers.length}</strong> employees
                </span>

                {searchQuery && (
                  <span className="employee-searching">
                    Searching for “{searchQuery}”
                  </span>
                )}
              </div>

              {/* TABLE */}

              <div className="employee-table-scroll">
                <table className="employee-table">
                  <thead>
                    <tr>
                      <th>Employee</th>

                      <th>Username</th>

                      <th className="hide-mobile">Email</th>

                      <th className="hide-tablet">Phone</th>

                      <th className="hide-tablet">Role</th>

                      <th className="employee-actions-heading">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentUsers.length === 0 ? (
                      <tr>
                        <td colSpan="6">
                          <div className="employee-empty">
                            <BsSearch />

                            <strong>No employees found</strong>

                            <span>Try changing your search.</span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      currentUsers.map((user) => {
                        const imageUrl = getImageUrl(user.imageUrl);

                        return (
                          <tr key={user.id}>
                            {/* EMPLOYEE */}

                            <td>
                              <div className="employee-profile">
                                {imageUrl ? (
                                  <button
                                    type="button"
                                    className="employee-avatar-button"
                                    onClick={() =>
                                      setEnlargedImageUrl(imageUrl)
                                    }
                                    aria-label={`View ${
                                      user.name || "employee"
                                    } photo`}
                                  >
                                    <img
                                      src={imageUrl}
                                      alt={user.name || "Employee"}
                                      className="employee-avatar"
                                    />
                                  </button>
                                ) : (
                                  <div className="employee-avatar-placeholder">
                                    <BsPersonFill />
                                  </div>
                                )}

                                <div className="employee-profile-details">
                                  <strong>
                                    {user.name || "Unnamed Employee"}
                                  </strong>

                                  <span className="employee-mobile-email">
                                    {user.email || "—"}
                                  </span>
                                </div>
                              </div>
                            </td>

                            {/* USERNAME */}

                            <td>
                              <span className="employee-type">
                                {user.username || "—"}
                              </span>
                            </td>

                            {/* EMAIL */}

                            <td className="hide-mobile">
                              {user.email ? (
                                <a
                                  href={`mailto:${user.email}`}
                                  className="employee-email"
                                >
                                  {user.email}
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>

                            {/* PHONE */}

                            <td className="hide-tablet">{user.phone || "—"}</td>

                            {/* ROLE */}

                            <td className="hide-tablet">
                              <span className="employee-role">
                                {user.role || "—"}
                              </span>
                            </td>

                            {/* ACTIONS */}

                            <td className="employee-table-actions">
                              <Link
                                to={`/employees/view/${user.id}`}
                                className="employee-action view"
                                aria-label="View employee"
                                title="View employee"
                              >
                                <BsEye />
                              </Link>

                              <Link
                                to={`/employees/update/${user.id}`}
                                className="employee-action edit"
                                aria-label="Edit employee"
                                title="Edit employee"
                              >
                                <BsPencilSquare />
                              </Link>

                              <button
                                type="button"
                                className="employee-action delete"
                                onClick={() => onDelete(user)}
                                aria-label="Delete employee"
                                title="Delete employee"
                              >
                                <BsTrash />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}

              {totalPages > 1 && (
                <div className="employee-pagination">
                  <span>
                    Page <strong>{currentPage}</strong> of{" "}
                    <strong>{totalPages}</strong>
                  </span>

                  <div className="employee-pagination-buttons">
                    <button
                      type="button"
                      className="employee-btn ghost"
                      disabled={currentPage === 1}
                      onClick={() =>
                        setCurrentPage((previous) => Math.max(previous - 1, 1))
                      }
                    >
                      Previous
                    </button>

                    <button
                      type="button"
                      className="employee-btn ghost"
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setCurrentPage((previous) =>
                          Math.min(previous + 1, totalPages),
                        )
                      }
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      </main>

      {/* IMAGE OVERLAY */}

      {enlargedImageUrl && (
        <div
          className="employee-image-overlay"
          onClick={() => setEnlargedImageUrl(null)}
        >
          <button
            type="button"
            className="employee-image-close"
            onClick={() => setEnlargedImageUrl(null)}
            aria-label="Close image"
          >
            <BsX />
          </button>

          <img
            src={enlargedImageUrl}
            alt="Employee"
            className="employee-image-enlarged"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default UserList;
